const SPINE_STAGES = {
  architecture_pending: {
    label: 'Pendiente de Architect',
    role: 'Architect',
    action: 'Preparar Architecture Spec',
    detail: 'Architect debe preparar la Architecture Spec antes de continuar.',
    color: 'amber',
  },
  architecture_review: {
    label: 'Arquitectura en revisión',
    role: 'Architect',
    action: 'Revisar y aprobar arquitectura',
    detail: 'Architect debe revisar el diseño y sus decisiones técnicas.',
    color: 'amber',
  },
  quality_pending: {
    label: 'Pendiente de Quality Guardian',
    role: 'Quality Guardian',
    action: 'Generar Test Spec y gates',
    detail: 'Quality Guardian debe definir pruebas, gates y el módulo de Runbook.',
    color: 'amber',
  },
  forge_master_pending: {
    label: 'Pendiente de Forge Master',
    role: 'Forge Master',
    action: 'Validar Execution Packet',
    detail: 'Forge Master debe validar el paquete antes de entregarlo a Builder.',
    color: 'amber',
  },
  builder_ready: {
    label: 'Lista para Builder',
    role: 'Builder',
    action: 'Reclamar paquete listo',
    detail: 'El Execution Packet, las pruebas y el Runbook están listos para ejecutar.',
    color: 'cyan',
  },
  build_planning: {
    label: 'Preparando construcción',
    role: 'Builder',
    action: 'Revisar Builder Brief y preparar tareas',
    detail: 'Builder revisa el contexto aprobado y propone el plan antes de iniciar agentes.',
    color: 'amber',
  },
  in_execution: {
    label: 'En ejecución',
    role: 'Builder',
    action: 'Ejecutar y entregar evidencia',
    detail: 'Builder implementa el paquete aprobado y registra evidencia de CI/CD.',
    color: 'cyan',
  },
  completed: {
    label: 'Completada',
    role: null,
    action: 'Entrega completada',
    detail: 'La ejecución y su evidencia fueron completadas.',
    color: 'green',
  },
  escalated: {
    label: 'Escalada',
    role: 'Forge Master',
    action: 'Resolver escalación',
    detail: 'Existe una decisión o excepción que requiere atención humana.',
    color: 'red',
  },
};

const GENERIC_STAGES = {
  clarifying: { label: 'En aclaración', color: 'amber' },
  ready: { label: 'Lista para revisión', color: 'cyan' },
  approved: { label: 'Aprobada', color: 'green' },
  rejected: { label: 'Rechazada', color: 'red' },
};

const ROLE_LABELS = {
  CONTEXT_OWNER: 'Context Owner',
  FORGE_MASTER: 'Forge Master',
  ARCHITECT: 'Architect',
  QUALITY_GUARDIAN: 'Quality Guardian',
  BUILDER: 'Builder',
};

function action(ownerIds, key, label, detail, node = null) {
  return {
    key,
    label,
    detail,
    node,
    ownerIds,
    owner: ownerIds.map((role) => ROLE_LABELS[role] || role).join(' y '),
  };
}

/**
 * The Project Spine exposes one actionable state for the project. Keeping this
 * calculation here prevents Work and ProjectSpine from prioritising different
 * nodes or roles for the same repository.
 */
export function nextProjectAction(spineData) {
  const intent = spineData?.business_intents?.[0];
  const spine = spineData?.spine;
  const nodes = intent?.nodes || [];

  if (!intent) return action(['CONTEXT_OWNER'], 'define_intent', 'Iniciar Business Intent', 'Comparte la intención del proyecto para iniciar.');
  if (intent.planning_findings?.some((finding) => finding.blocking)) return action(['CONTEXT_OWNER', 'FORGE_MASTER'], 'resolve_findings', 'Resolver aclaraciones', 'Resuelvan las aclaraciones que bloquean el mapa.');
  if (!intent.context_owner_approved) return action(['CONTEXT_OWNER'], 'approve_context', 'Aprobar Business Intent', 'Context Owner debe aprobar la intención antes de generar el mapa.');
  if (!intent.forge_master_approved) return action(['FORGE_MASTER'], 'approve_forge_master', 'Aprobar Business Intent', 'Forge Master debe aprobar la intención antes de generar el mapa.');
  if (!intent.map_approved && nodes.length === 0) {
    return action(['CONTEXT_OWNER', 'FORGE_MASTER'], 'generate_map', 'Regenerar mapa de Forge Specs', 'Las aclaraciones están resueltas; regeneren el mapa antes de aprobarlo.');
  }
  if (!intent.map_approved) return action(['CONTEXT_OWNER', 'FORGE_MASTER'], 'approve_map', 'Aprobar mapa de Forge Specs', 'Aprueben el mapa de Forge Specs para pasar a diseño.');

  const planningNode = nodes.find((node) => node.status === 'build_planning');
  if (planningNode) return action(['BUILDER'], 'build_planning', 'Preparar construcción', `Builder: prepara ${planningNode.title} con el contexto aprobado`, planningNode);

  const executingNode = nodes.find((node) => node.status === 'in_execution');
  if (executingNode?.delivery_state?.tasks_ready && executingNode.delivery_state.evidence_ready && !executingNode.delivery_state.quality_approved) {
    return action(['QUALITY_GUARDIAN'], 'delivery_quality_review', 'Revisar calidad para entrega', `Quality Guardian: revisa ${executingNode.title} antes del cierre.`, executingNode);
  }
  if (executingNode) return action(['BUILDER'], 'in_execution', 'Ver paquete en ejecución', `Builder: ${executingNode.title} está en ejecución.`, executingNode);

  const builderNode = nodes.find((node) => node.status === 'builder_ready');
  if (builderNode) {
    const availability = builderAvailability(spineData, builderNode);
    return availability.blocked
      ? action(['BUILDER'], 'builder_blocked', 'Ver dependencia pendiente', `Builder: ${builderNode.title} espera una dependencia sin completar`, builderNode)
      : action(['BUILDER'], 'builder_ready', 'Reclamar paquete listo', `Builder: ${builderNode.title}`, builderNode);
  }

  if (Object.values(spine?.stack || {}).filter(Boolean).length === 0) return action(['CONTEXT_OWNER', 'FORGE_MASTER'], 'apply_stack', 'Aplicar stack sugerido', 'FORGE comparará el stack sugerido con el alcance aprobado.');
  if (spine?.stack_scope_decision?.status === 'pending') return action(['ARCHITECT'], 'approve_stack_scope', 'Aprobar componentes no aplicables', 'Architect debe justificar las desviaciones entre el stack global y el alcance aprobado.');
  if (!spine?.global_diagram_artifacts) return action(['ARCHITECT'], 'generate_diagram', 'Generar diagrama global', 'Preflight técnico: el diagrama global es obligatorio.');
  if (!spine?.preflight_approved) return action(['ARCHITECT'], 'approve_preflight', 'Aprobar diagrama actual y habilitar preflight', 'Architect puede continuar ahora o registrar mejoras opcionales del diagrama.');

  const reviewNode = nodes.find((node) => node.status === 'architecture_review');
  if (reviewNode) return action(['ARCHITECT'], 'architecture_review', 'Revisar Architecture Spec', `Architect: revisa ${reviewNode.title}`, reviewNode);
  const qualityNode = nodes.find((node) => node.status === 'quality_pending');
  if (qualityNode) return action(['QUALITY_GUARDIAN'], 'quality_pending', 'Generar Test Spec y gates', `Quality Guardian: ${qualityNode.title}`, qualityNode);
  const packetNode = nodes.find((node) => node.status === 'forge_master_pending');
  if (packetNode) return action(['FORGE_MASTER'], 'forge_master_pending', 'Validar Execution Packet', `Forge Master: ${packetNode.title}`, packetNode);
  const architectureNode = nodes.find((node) => node.status === 'architecture_pending');
  if (architectureNode) return action(['ARCHITECT'], 'architecture_pending', 'Preparar Architecture Spec', `Architect: ${architectureNode.title}`, architectureNode);
  if (nodes.some((node) => node.completed)) return action(['QUALITY_GUARDIAN', 'FORGE_MASTER'], 'review_delivery', 'Revisar entrega', 'Revisen la evidencia y el cierre de la entrega.');
  return null;
}

export function diagramImprovementCount(history = []) {
  return Math.min(3, history.filter((item) => item?.improvement_report).length);
}

export function projectActionBlockMessage(actionState, roles = [], canAdmin = false) {
  if (!actionState || canAdmin || actionState.ownerIds.some((role) => roles.includes(role))) return null;
  return `Bloqueado: requiere el rol ${actionState.owner} para ${actionState.label}.`;
}

export function spineNodesBySession(spineData) {
  const nodes = (spineData?.business_intents || []).flatMap((intent) => intent.nodes || []);
  return new Map(nodes.flatMap((node) => [
    node.spec_session_id,
    node.architecture_spec_session_id,
    node.test_spec_session_id,
  ].filter(Boolean).map((sessionId) => [Number(sessionId), node])));
}

export function spineNodeForSession(spineData, sessionId) {
  return spineNodesBySession(spineData).get(Number(sessionId)) || null;
}

export function stageFor({ session, node }) {
  if (node) {
    const key = node.completed ? 'completed' : node.status;
    return { key, spine: true, ...(SPINE_STAGES[key] || {
      label: 'Estado pendiente de revisión',
      role: 'Forge Master',
      action: 'Revisar el estado de la Spec',
      detail: 'FORGE detectó un estado que requiere validación del equipo.',
      color: 'steel',
    }) };
  }
  const key = session?.status || 'clarifying';
  return { key, spine: false, ...(GENERIC_STAGES[key] || { label: 'Estado pendiente de revisión', color: 'steel' }) };
}

export function spineSessionIds(spineData) {
  return new Set([...spineNodesBySession(spineData).keys()]);
}

export function isPrimarySpineSession(node, sessionId) {
  return Boolean(node) && Number(node.spec_session_id) === Number(sessionId);
}

function nodesForNode(spineData, targetNode) {
  return (spineData?.business_intents || [])
    .find((intent) => (intent.nodes || []).some((node) => node.id === targetNode?.id))
    ?.nodes || [];
}

export function builderAvailability(spineData, node) {
  const dependencyPositions = new Set(node?.depends_on || []);
  const pendingDependencies = nodesForNode(spineData, node)
    .filter((candidate) => dependencyPositions.has(candidate.position) && !candidate.completed)
    .map((candidate) => ({ node: candidate, stage: stageFor({ node: candidate }) }));

  return {
    pendingDependencies,
    blocked: pendingDependencies.length > 0,
    available: Boolean(node?.execution_packet_markdown) && pendingDependencies.length === 0,
  };
}
